<template>
  <b-card v-if="isVisible" no-body>
    <b-card-header class="pb-50">
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="mb-0">Свои плейлисты</h5>
        <div class="d-flex align-items-center">
          <b-badge v-if="poolData && poolData.legacy" variant="light-warning" class="mr-50">Старый формат</b-badge>
          <small v-if="poolData && poolData.source" class="text-muted">
            Источник: {{ poolSourceLabel(poolData.source) }}
          </small>
        </div>
      </div>
    </b-card-header>
    <b-card-body>
      <!-- Статистика резолва из конфига пака -->
      <div v-if="pool" class="mb-1">
        <div class="d-flex flex-wrap align-items-center">
          <div
              v-for="statItem in statItems"
              :key="statItem.key"
              class="custom-pool-stat mr-1 mb-50"
          >
            <div class="text-muted font-small-2">{{ statItem.label }}</div>
            <div class="font-weight-bold" :class="statItem.textClass">{{ statItem.value }}</div>
          </div>
        </div>
        <div class="d-flex flex-wrap align-items-center font-small-3">
          <b-badge
              :variant="pool.themesByPlayer ? 'light-success' : 'light-secondary'"
              class="mr-50 mb-25"
          >
            Темы по игрокам: {{ pool.themesByPlayer ? 'Да' : 'Нет' }}
          </b-badge>
          <b-badge
              :variant="isExactOnly ? 'light-success' : 'light-secondary'"
              class="mr-50 mb-25"
          >
            Только точное совпадение: {{ isExactOnly ? 'Да' : 'Нет' }}
          </b-badge>
          <span v-if="pool.resolvedAt" class="text-muted mr-1 mb-25">
            Проверено: {{ formatDate(pool.resolvedAt) }}
          </span>
          <small v-if="pool.poolId" class="text-muted mb-25">poolId: <code>{{ pool.poolId }}</code></small>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-1">
        <b-spinner small class="text-primary" />
      </div>

      <b-alert v-else-if="loadError" show variant="warning" class="mb-0">
        <div class="alert-body">{{ loadError }}</div>
      </b-alert>

      <p v-else-if="!entryGroups.length" class="text-muted mb-0">
        Записи пула не найдены
      </p>

      <!-- Записи, сгруппированные по игроку -->
      <div v-for="group in entryGroups" :key="group.owner" class="mt-1">
        <h6 class="mb-50">
          <feather-icon icon="UserIcon" class="mr-25" />
          {{ group.owner }}
          <span class="text-muted font-small-3">({{ group.entries.length }})</span>
        </h6>

        <div
            v-for="entryState in group.entries"
            :key="entryState.key"
            class="custom-pool-entry border rounded p-1 mb-1"
        >
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <div class="d-flex align-items-center flex-wrap custom-pool-entry-title">
              <b-badge :variant="entryState.badgeVariant" class="mr-50">
                <feather-icon :icon="entryState.icon" class="mr-25" />
                {{ entryState.label }}
              </b-badge>

              <b-link
                  v-if="entryState.kind === 'playlist'"
                  :href="entryState.value"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-break"
              >
                {{ entryState.value }}
                <feather-icon icon="ExternalLinkIcon" size="12" />
              </b-link>
              <b-button
                  v-else
                  variant="flat-secondary"
                  size="sm"
                  class="px-50"
                  @click="entryState.isTextOpen = !entryState.isTextOpen"
              >
                <feather-icon :icon="entryState.isTextOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" class="mr-25" />
                {{ entryState.isTextOpen ? 'Скрыть' : 'Показать' }}
              </b-button>
            </div>

            <div class="d-flex align-items-center">
              <b-button
                  v-if="entryState.check.isShown"
                  variant="flat-secondary"
                  size="sm"
                  class="mr-50"
                  @click="entryState.check.isShown = false"
              >
                Свернуть
              </b-button>
              <b-button
                  v-if="canCheckEntry(entryState)"
                  variant="outline-primary"
                  size="sm"
                  :disabled="entryState.check.isLoading"
                  @click="runCheck(entryState)"
              >
                <b-spinner v-if="entryState.check.isLoading" small class="mr-25" />
                <feather-icon v-else icon="DatabaseIcon" class="mr-25" />
                Проверить по БД
              </b-button>
            </div>
          </div>

          <!-- Содержимое текстовых списков -->
          <b-collapse v-if="entryState.kind !== 'playlist'" v-model="entryState.isTextOpen">
            <pre class="custom-pool-text mt-50 mb-0">{{ entryState.value }}</pre>
          </b-collapse>

          <!-- Результат проверки плейлиста / списка треков -->
          <div v-if="entryState.check.isShown && entryState.check.importResult" class="mt-1">
            <div class="d-flex flex-wrap align-items-center mb-50 font-small-3">
              <span v-if="entryState.check.importResult.title" class="font-weight-bold mr-1">
                {{ entryState.check.importResult.title }}
              </span>
              <span class="mr-1">Треков: <strong>{{ importTracks(entryState).length }}</strong></span>
              <span class="mr-1">
                Без артиста в БД:
                <strong :class="{ 'text-warning': countTracksWithoutArtist(entryState) }">
                  {{ countTracksWithoutArtist(entryState) }}
                </strong>
              </span>
              <span>
                Без трека в БД:
                <strong :class="{ 'text-warning': countTracksWithoutTrack(entryState) }">
                  {{ countTracksWithoutTrack(entryState) }}
                </strong>
              </span>
            </div>
            <playlist-tracks-table
                :tracks="importTracks(entryState)"
                :is-loading="isAddingTrack"
                @add-track="track => onAddTrack(entryState, track)"
            />
          </div>

          <!-- Результат проверки списка артистов -->
          <div v-if="entryState.check.isShown && entryState.check.artistRows" class="mt-1">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-50 font-small-3">
              <div>
                Артистов: <strong>{{ entryState.check.artistRows.length }}</strong>,
                нет в БД:
                <strong :class="{ 'text-warning': countMissingArtists(entryState) }">
                  {{ countMissingArtists(entryState) }}
                </strong>
              </div>
              <b-form-checkbox v-model="entryState.check.isOnlyMissing" switch>
                Только отсутствующие
              </b-form-checkbox>
            </div>
            <b-table-simple small responsive class="mb-0">
              <b-thead>
                <b-tr>
                  <b-th class="text-nowrap">
                    Артист
                    <b-link
                        v-for="sortOption in externalStatsSortOptions"
                        :key="sortOption.key"
                        class="ml-75"
                        :class="{ 'font-weight-bolder': entryState.check.sortBy === sortOption.key }"
                        :title="`Сортировать по: ${sortOption.title}`"
                        @click="toggleArtistSort(entryState, sortOption.key)"
                    >
                      <source-icon :source="sortOption.key" :size="14" />
                      <feather-icon :icon="sortIcon(entryState, sortOption.key)" size="12" />
                    </b-link>
                  </b-th>
                  <b-th>Статус</b-th>
                  <b-th v-if="$can('manage', 'artist')" style="width: 1%" />
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="(artistRow, artistIndex) in visibleArtistRows(entryState)" :key="artistIndex">
                  <b-td>
                    {{ artistRow.name }}
                    <template v-if="!artistRow.id">
                      <b-button
                          :to="{ name: 'artists-search', query: { q: artistRow.name } }"
                          variant="info"
                          class="btn-icon ml-1"
                          size="sm"
                      >
                        <feather-icon icon="SearchIcon"/>
                      </b-button>
                      <!-- Популярность во внешних сервисах — справа от поиска, только для отсутствующих в БД -->
                      <artist-external-stats-badge
                          :stats="statsByName[artistStatsKey(artistRow.name)]"
                          :loading="Boolean(loadingNames[artistStatsKey(artistRow.name)])"
                          :original-name="artistRow.name"
                      />
                    </template>
                  </b-td>
                  <b-td>
                    <b-link
                        v-if="artistRow.id"
                        :to="{ name: 'artist-view', params: { id: artistRow.id } }"
                        class="font-weight-bold"
                    >
                      {{ artistRow.title || artistRow.name }}
                    </b-link>
                    <b-badge v-else-if="artistRow.isSubmitted" variant="light-warning">На одобрении</b-badge>
                    <b-badge v-else variant="light-danger">Нет в БД</b-badge>
                  </b-td>
                  <b-td v-if="$can('manage', 'artist')" class="text-nowrap">
                    <b-button
                        v-if="!artistRow.id && !artistRow.isSubmitted"
                        variant="outline-success"
                        size="sm"
                        :disabled="artistRow.isAdding"
                        @click="addArtist(artistRow)"
                    >
                      <b-spinner v-if="artistRow.isAdding" small class="mr-25" />
                      <feather-icon v-else icon="PlusIcon" class="mr-25" />
                      Добавить артиста
                    </b-button>
                  </b-td>
                </b-tr>
                <b-tr v-if="!visibleArtistRows(entryState).length">
                  <b-td colspan="3" class="text-muted text-center">Все артисты есть в БД</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCollapse,
  BFormCheckbox,
  BLink,
  BSpinner,
  BTableSimple,
  BTbody,
  BTd,
  BTh,
  BThead,
  BTr,
} from 'bootstrap-vue'
import Vue from 'vue'
import { computed, onUnmounted, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { isSubmissionResponse, notifySubmitted } from '@/libs/moderation'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import ArtistExternalStatsBadge from '@/views/db-music/ArtistExternalStatsBadge.vue'
import PlaylistTracksTable from '@/views/db-music/PlaylistTracksTable.vue'
import SourceIcon from '@/views/db-music/SourceIcon.vue'
import useArtistExternalStats, { artistStatsKey } from '@/views/db-music/useArtistExternalStats'
import usePlaylistTrackAdder from '@/views/db-music/usePlaylistTrackAdder'

const NO_OWNER_LABEL = 'Без игрока'

const POOL_SOURCE_LABELS = {
  file: 'файл',
  config: 'конфиг пака',
}

const getErrorText = error => (error.response && error.response.data && (error.response.data.message || error.response.data.error))
  || error.message
  || 'Произошла ошибка'

const splitLines = text => String(text || '')
  .split(/\r?\n/)
  .map(line => line.trim())
  .filter(line => line !== '')

// Источник импорта по домену ссылки на плейлист
const detectPlaylistSource = url => {
  const link = String(url || '').toLowerCase()
  if (link.includes('music.yandex')) return 'yandex'
  if (link.includes('spotify.com')) return 'spotify'
  return null
}

// Импорт текстом понимает « - », « – » и « — », но первым ищет « - »:
// в строке «Исполнитель — Название - Remix» разрез ушёл бы не туда, поэтому первое длинное тире приводим к дефису
const normalizeTrackLine = line => (line.includes(' - ') ? line : line.replace(/ [—–] /, ' - '))

// «1 строка», «2 строки», «5 строк»
const pluralLines = count => {
  const lastTwo = count % 100
  const last = count % 10
  if (lastTwo >= 11 && lastTwo <= 14) return `${count} строк`
  if (last === 1) return `${count} строка`
  if (last >= 2 && last <= 4) return `${count} строки`
  return `${count} строк`
}

// Сортировка таблицы артистов по внешней популярности
const EXTERNAL_STATS_SORT_OPTIONS = [
  { key: 'spotify', label: 'Spotify', title: 'популярности в Spotify' },
  { key: 'lastfm', label: 'Last.fm', title: 'слушателям Last.fm' },
]

const externalStatsSortValue = (stats, sortBy) => {
  if (!stats) return null
  if (sortBy === 'spotify') return stats.spotify ? stats.spotify.popularity : null
  if (sortBy === 'lastfm') return stats.lastfm ? stats.lastfm.listeners : null
  return null
}

const describeEntry = entry => {
  if (entry.kind === 'playlist') {
    const source = detectPlaylistSource(entry.value)
    if (source === 'yandex') return { label: 'Плейлист Яндекс', icon: 'MusicIcon', badgeVariant: 'light-warning' }
    if (source === 'spotify') return { label: 'Плейлист Spotify', icon: 'MusicIcon', badgeVariant: 'light-success' }
    return { label: 'Плейлист', icon: 'MusicIcon', badgeVariant: 'light-secondary' }
  }
  const lineCount = splitLines(entry.value).length
  if (entry.kind === 'artists') return { label: `Список артистов (${pluralLines(lineCount)})`, icon: 'UsersIcon', badgeVariant: 'light-primary' }
  if (entry.kind === 'tracks') return { label: `Список треков (${pluralLines(lineCount)})`, icon: 'ListIcon', badgeVariant: 'light-info' }
  return { label: entry.kind, icon: 'FileTextIcon', badgeVariant: 'light-secondary' }
}

export default {
  components: {
    ArtistExternalStatsBadge,
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCollapse,
    BFormCheckbox,
    BLink,
    BSpinner,
    BTableSimple,
    BTbody,
    BTd,
    BTh,
    BThead,
    BTr,
    PlaylistTracksTable,
    SourceIcon,
  },
  props: {
    packId: {
      type: [Number, String],
      required: true,
    },
    // config.options.customPool пака: {enabled, poolId, resolvedAt, stats, themesByPlayer, exactOnly}
    pool: {
      type: Object,
      default: null,
    },
  },
  setup(props, { root }) {
    const toast = useToast()

    // Модуль `playlist` нужен для импорта и добавления треков. Снимаем регистрацию,
    // только если регистрировали сами — чтобы не отобрать модуль у другой смонтированной страницы
    const PLAYLIST_STORE_MODULE_NAME = 'playlist'
    let isPlaylistModuleRegisteredHere = false
    if (!store.hasModule(PLAYLIST_STORE_MODULE_NAME)) {
      store.registerModule(PLAYLIST_STORE_MODULE_NAME, musicStoreModule)
      isPlaylistModuleRegisteredHere = true
    }
    onUnmounted(() => {
      if (isPlaylistModuleRegisteredHere && store.hasModule(PLAYLIST_STORE_MODULE_NAME)) {
        store.unregisterModule(PLAYLIST_STORE_MODULE_NAME)
      }
    })

    const poolData = ref(null)
    const entryStates = ref([])
    const isLoading = ref(true)
    const loadError = ref(null)

    const isVisible = computed(() => Boolean((props.pool && props.pool.enabled) || poolData.value))
    const isExactOnly = computed(() => Boolean((props.pool && props.pool.exactOnly) || (poolData.value && poolData.value.exact_only)))

    const statItems = computed(() => {
      const stats = (props.pool && props.pool.stats) || {}
      const items = [
        { key: 'playlists', label: 'Плейлистов' },
        { key: 'tracksSeen', label: 'Треков просмотрено' },
        { key: 'artistsSeen', label: 'Артистов' },
        { key: 'levelA', label: 'A — точные треки', textClass: 'text-success' },
        { key: 'levelB', label: 'B — артисты', textClass: 'text-primary' },
        { key: 'levelV', label: 'V — похожие', textClass: 'text-info', isOptional: true },
        { key: 'unmatched', label: 'Не сопоставлено', textClass: stats.unmatched ? 'text-warning' : '' },
      ]
      return items
        .filter(item => !item.isOptional || (stats[item.key] !== undefined && stats[item.key] !== null))
        .map(item => ({
          ...item,
          value: stats[item.key] !== undefined && stats[item.key] !== null ? stats[item.key] : '—',
        }))
    })

    const entryGroups = computed(() => {
      const groups = []
      const groupByOwner = {}
      entryStates.value.forEach(entryState => {
        const owner = entryState.owner || NO_OWNER_LABEL
        if (!groupByOwner[owner]) {
          groupByOwner[owner] = { owner, entries: [] }
          groups.push(groupByOwner[owner])
        }
        groupByOwner[owner].entries.push(entryState)
      })
      return groups
    })

    // Все поля состояния заводим сразу — иначе Vue 2 не отследит их изменение
    const buildEntryState = (entry, index) => ({
      key: index,
      kind: entry.kind,
      value: entry.value,
      owner: entry.owner,
      ...describeEntry(entry),
      isTextOpen: false,
      check: {
        isLoading: false,
        isShown: false,
        importResult: null,
        artistRows: null,
        isOnlyMissing: false,
        // null | 'spotify' | 'lastfm'
        sortBy: null,
        isSortDescending: true,
      },
    })

    store.dispatch('app-generator/fetchCustomPool', { id: props.packId })
      .then(response => {
        poolData.value = response.data.data || null
        const entries = (poolData.value && Array.isArray(poolData.value.entries)) ? poolData.value.entries : []
        entryStates.value = entries.map(buildEntryState)
      })
      .catch(error => {
        loadError.value = `Не удалось загрузить записи пула: ${getErrorText(error)}`
      })
      .finally(() => {
        isLoading.value = false
      })

    const can = (action, subject) => (root.$can ? root.$can(action, subject) : true)

    // Импорт плейлиста/текста требует db-music.manage, сверка артистов — db-music.view
    const canCheckEntry = entryState => {
      if (entryState.kind === 'artists') return can('view', 'db-music')
      if (entryState.kind === 'playlist') return can('manage', 'db-music') && Boolean(detectPlaylistSource(entryState.value))
      if (entryState.kind === 'tracks') return can('manage', 'db-music')
      return false
    }

    const showErrorToast = (title, error) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          text: getErrorText(error),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    const buildImportPayload = entryState => {
      if (entryState.kind === 'playlist') {
        return { source: detectPlaylistSource(entryState.value), link: entryState.value }
      }
      return { source: 'text', text: splitLines(entryState.value).map(normalizeTrackLine).join('\n') }
    }

    const checkImport = async entryState => {
      const response = await store.dispatch('playlist/playlistImport', buildImportPayload(entryState))
      entryState.check.importResult = response.data
    }

    // --- Популярность отсутствующих артистов в Spotify / Last.fm ---
    const { statsByName, loadingNames, loadFor } = useArtistExternalStats()

    // Новый прогон отменяет предыдущий, поэтому собираем отсутствующих со всех проверенных списков
    const loadExternalStatsForMissingArtists = () => {
      const names = []
      entryStates.value.forEach(entryState => {
        (entryState.check.artistRows || []).forEach(row => {
          if (!row.id) names.push(row.name)
        })
      })
      if (names.length) loadFor(names)
    }

    const checkArtists = async entryState => {
      const names = splitLines(entryState.value)
      const response = await store.dispatch('app-generator/matchArtists', { names })
      entryState.check.artistRows = (response.data.data || []).map(row => ({
        name: row.name,
        id: row.id,
        title: row.title,
        isAdding: false,
        isSubmitted: false,
      }))
      loadExternalStatsForMissingArtists()
    }

    const runCheck = async entryState => {
      entryState.check.isLoading = true
      try {
        if (entryState.kind === 'artists') await checkArtists(entryState)
        else await checkImport(entryState)
        entryState.check.isShown = true
      } catch (error) {
        showErrorToast('Не удалось проверить по БД', error)
      } finally {
        entryState.check.isLoading = false
      }
    }

    // --- Добавление треков из результата проверки ---
    const isAddingTrack = ref(false)
    let entryStateOfAddedTrack = null

    const { addTrack } = usePlaylistTrackAdder({
      toast,
      isLoading: isAddingTrack,
      reloadPlaylist: () => (entryStateOfAddedTrack ? checkImport(entryStateOfAddedTrack) : null),
    })

    const onAddTrack = (entryState, track) => {
      entryStateOfAddedTrack = entryState
      return addTrack(track)
    }

    // --- Добавление артистов ---
    const addArtist = async artistRow => {
      Vue.set(artistRow, 'isAdding', true)
      try {
        const response = await store.dispatch('playlist/createArtist', { title: artistRow.name })
        if (isSubmissionResponse(response)) {
          Vue.set(artistRow, 'isSubmitted', true)
          notifySubmitted(toast)
          return
        }
        const artist = response.data.data || response.data
        Vue.set(artistRow, 'id', artist.id)
        Vue.set(artistRow, 'title', artist.title || artistRow.name)
        toast({
          component: ToastificationContent,
          props: {
            title: `Артист «${artistRow.name}» добавлен`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        showErrorToast('Не удалось добавить артиста', error)
      } finally {
        Vue.set(artistRow, 'isAdding', false)
      }
    }

    // --- Хелперы шаблона ---
    const importTracks = entryState => (entryState.check.importResult && entryState.check.importResult.tracks) || []
    const countTracksWithoutArtist = entryState => importTracks(entryState)
      .filter(track => !(track.artists || []).some(artist => artist.id)).length
    const countTracksWithoutTrack = entryState => importTracks(entryState).filter(track => !track.id).length
    const countMissingArtists = entryState => (entryState.check.artistRows || []).filter(row => !row.id).length
    const visibleArtistRows = entryState => {
      const rows = entryState.check.artistRows || []
      const filteredRows = entryState.check.isOnlyMissing ? rows.filter(row => !row.id) : rows
      const { sortBy, isSortDescending } = entryState.check
      if (!sortBy) return filteredRows

      // Артисты без значения (есть в БД, не найдены, ещё грузятся) — всегда в конце
      const direction = isSortDescending ? -1 : 1
      return filteredRows
        .map((row, index) => ({
          row,
          index,
          value: row.id ? null : externalStatsSortValue(statsByName.value[artistStatsKey(row.name)], sortBy),
        }))
        .sort((left, right) => {
          const isLeftEmpty = left.value === null || left.value === undefined
          const isRightEmpty = right.value === null || right.value === undefined
          if (isLeftEmpty && isRightEmpty) return left.index - right.index
          if (isLeftEmpty) return 1
          if (isRightEmpty) return -1
          return (left.value - right.value) * direction || left.index - right.index
        })
        .map(item => item.row)
    }

    // Клик по колонке: по убыванию → по возрастанию → без сортировки
    const toggleArtistSort = (entryState, sortBy) => {
      const { check } = entryState
      if (check.sortBy !== sortBy) {
        check.sortBy = sortBy
        check.isSortDescending = true
      } else if (check.isSortDescending) {
        check.isSortDescending = false
      } else {
        check.sortBy = null
        check.isSortDescending = true
      }
    }

    const sortIcon = (entryState, sortBy) => {
      if (entryState.check.sortBy !== sortBy) return 'ChevronsDownIcon'
      return entryState.check.isSortDescending ? 'ArrowDownIcon' : 'ArrowUpIcon'
    }

    const formatDate = value => (value ? new Date(value).toLocaleString('ru-RU') : '—')
    const poolSourceLabel = source => POOL_SOURCE_LABELS[source] || source

    return {
      poolData,
      isLoading,
      loadError,
      isVisible,
      isExactOnly,
      statItems,
      entryGroups,
      isAddingTrack,

      canCheckEntry,
      runCheck,
      onAddTrack,
      addArtist,
      importTracks,
      countTracksWithoutArtist,
      countTracksWithoutTrack,
      countMissingArtists,
      visibleArtistRows,
      toggleArtistSort,
      sortIcon,
      externalStatsSortOptions: EXTERNAL_STATS_SORT_OPTIONS,
      statsByName,
      loadingNames,
      artistStatsKey,
      formatDate,
      poolSourceLabel,
    }
  },
}
</script>

<style lang="scss" scoped>
.custom-pool-stat {
  min-width: 110px;
  padding: 0.35rem 0.75rem;
  border-radius: 0.357rem;
  background-color: rgba(115, 103, 240, 0.08);
}

.custom-pool-entry-title {
  min-width: 0;
  flex: 1 1 auto;
}

.custom-pool-text {
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
  font-size: 0.857rem;
}
</style>
